import { FC, useEffect, useState, useRef } from "react";
import { IProject } from "../../api/model/IProject";
import { getUser, login, User } from "../../api/AuthProvider";
import { Label } from "@fluentui/react/lib/Label";
import { Spinner } from "@fluentui/react/lib/Spinner";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import React = require("react");
import { Tabs } from "./Tabs";
import { getBillingInformation, setBillingInformation } from "../../api/GraphProvider";
import { Mapping } from "../../api/Mapping";
import { Abo } from "../../api/Abo";
import { IAbo } from "../../api/model/IAbo";
import { LinkCard } from "./LinkCard";
import { removeCategory, removeProperties } from "../../service/appointment.service";
import { Header } from "./Header";
import { createTheme, FluentTheme, ThemeProvider } from "@fluentui/react/lib/Theme";
import { dismissMessage, showMessage, useGlobalState } from "../../service/globalState.service";
import { MessageBar, MessageBarType } from "@fluentui/react/lib/MessageBar";


/* global require */

export interface AppProps {
  isOfficeInitialized: boolean;
}

export interface AppState {
  projects: IProject[];
  selectedProject?: IProject;
  user?: User;
  isLoggingIn: boolean;
}

const Centered = (comp: JSX.Element) => (
  <div style={{
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)'
  }}>
    {comp}
  </div>
)


export const DarkTheme = createTheme({
  palette: {
    themePrimary: '#34a6ed',
    themeLighterAlt: '#020709',
    themeLighter: '#081b26',
    themeLight: '#103247',
    themeTertiary: '#1f648e',
    themeSecondary: '#2e92d1',
    themeDarkAlt: '#47afef',
    themeDark: '#62bbf1',
    themeDarker: '#8accf5',
    neutralLighterAlt: '#323130',
    neutralLighter: '#5f5f5f',
    neutralLight: '#2f2e2d',
    neutralQuaternaryAlt: '#2c2b2a',
    neutralQuaternary: '#b8b8b8',
    neutralTertiaryAlt: '#282726',
    neutralTertiary: '#c8c8c8',
    neutralSecondary: '#d0d0d0',
    neutralPrimaryAlt: '#dadada',
    neutralPrimary: '#ffffff',
    neutralDark: '#f4f4f4',
    black: '#f8f8f8',
    white: '#262626',
  }
});

const App: FC<AppProps> = ({ isOfficeInitialized }) => {
  const [isLoggingIn, setLogginIn] = useState(true);
  const [user, setUser] = useState<User>();
  const [linkedAbo, setLinkedAbo] = React.useState<IAbo>();
  const [{ darkTheme }] = useGlobalState('settings');
  const [message] = useGlobalState('message');


  useEffect(() => {
    fetchUser();
  }, [])

  const fetchUser = async () => {
    const user = await getUser();
    setLogginIn(false);
    setUser(user);

    if (!user) return;

    fetchLinkedAbo();
  }

  const _eventId = useRef<string>();
  const fetchLinkedAbo = async () => {
    const { item, convertToRestId } = Office.context.mailbox;

    item.saveAsync(async res => {
      const appointmentId = res.value;
      _eventId.current = convertToRestId(appointmentId, Office.MailboxEnums.RestVersion.v1_0);
      const appointmentGuid = await getBillingInformation(_eventId.current);
      if (!appointmentGuid) return;
      const mapping = await Mapping.get(appointmentGuid);
      const linkedAbo = await Abo.get(mapping.aboGuid);

      if (linkedAbo) setLinkedAbo(linkedAbo);
    })
  }

  const unlink = async () => {
    if (!_eventId.current) return;
    try {
      await setBillingInformation(_eventId.current, '');
      await removeCategory();
      await removeProperties();
      setLinkedAbo(null);
    } catch (e) {
      showMessage({
        type: MessageBarType.error,
        text: 'Error while unlinking task: ' + JSON.stringify(e)
      })
    }
  }

  const renderContent = () => {
    if (isLoggingIn) {
      return Centered(
        <>
          <Label>Logging In</Label>
          <Spinner />
        </>
      );
    }

    if (!user) {
      return Centered(<PrimaryButton text="Log In" onClick={login} />)
    }

    return (
      <div className="ms-welcome">
        <main className="ms-welcome__main">
          {message && (
            <MessageBar
              messageBarType={message.type}
              onDismiss={dismissMessage}
            >
              {message.text}
            </MessageBar>
          )}
          <Header user={user} />

          <br />
          {linkedAbo ?
            <>
              <Label>Appointment is <b>linked</b> to task:</Label>
              <br />
              <LinkCard
                task={linkedAbo}
                isLinked
                onClick={unlink}
              />
            </> :
            <Tabs />}
        </main>
      </div>
    );
  }
  return (
    <ThemeProvider applyTo="body" theme={darkTheme ? DarkTheme : FluentTheme}>
      {renderContent()}
    </ThemeProvider>
  );
}


export default App;